import {zodResolver} from '@hookform/resolvers/zod';
import {format} from 'date-fns';
import Cookies from 'js-cookie';
import {
	AbandonFlowModal,
	AlertActions,
	AlertModal,
	MainFooter,
	StyledGrid,
} from 'presentation/components';
import {ErrorOnCreateRequestModal} from 'presentation/components/ChangeData/ErrorOnCreateRequestModal';
import {DateFormatEnum, OperationStateTypesEnum} from 'presentation/constants';
import {useHostName} from 'presentation/hooks';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {FeaturesEnum} from 'presentation/constants/featuresFlagEnum';
import {useMediaQueries} from 'presentation/pages/private/hooks';
import {useGetBeneficiaryBankDataQuery} from 'presentation/pages/private/profile/queries/useGetBeneficiaryBankDataQuery';
import {InvoiceTypeEnum} from 'presentation/pages/private/refund/constants/invoiceTypeEnum';
import {useRefundRequest} from 'presentation/pages/private/refund/hooks';
import {useAccountDataStore} from 'presentation/pages/private/store/useAccountDataStateStore';
import {translations} from 'presentation/translations-new';
import {parseDate, validateCNPJ, validateCPF} from 'presentation/utils';
import React, {Fragment, useEffect, useState} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useAccordionExpandedStore} from '../../store/useAccordionExpandedStore';
import {useStepStore} from '../../store/useStepStore';
import {AccordionExpandedEnum, StepEnum} from '../../types';
import {FiscalDocument, FooterButton, PatientData, ProviderData} from './components';
import {PersonTypeEnum, SearchDoctorByEnum} from './components/ProviderData/constants/types';
import {OFFSETS_TO_SCROLL} from './constants/offsetsToScroll';
import {CustomContainer, CustomStyledGridItem, StyledFooterContainer} from './styles';
import {
	fiscalDocumentSchema,
	FiscalDocumentSchema,
	PatientDataSchema,
	patientDataSchema,
	ProviderDataSchema,
	providerDataSchema,
} from './validations';
import {useTherapies} from '../Therapies/hooks/useTherapies';

const {
	errors: {invalidCPF, invalidCNPJ},
} = translations['pt-br'].privateFlow.refund.receiptRegistrationPage.providerData;
const {discardModal} = translations['pt-br'].privateFlow.refund;
const {editDataAndCleanProceduresModal} = translations['pt-br'].privateFlow.refund.therapies;

type ReceiptRegistrationProps = {
	nextStepWhenNoTherapies?: StepEnum;
};

export const ReceiptRegistration = ({nextStepWhenNoTherapies}: ReceiptRegistrationProps = {}) => {
	const {
		atoms,
		handleUpdateAtoms,
		formAtoms,
		handleUpdateFormAtoms,
		handleCreateRequest,
		handleUpdateRequest,
		IS_LOADING,
		IS_ERROR,
		requests,
		handleAddNewRequestInProtocol,
		handleDeleteLastRequest,
		setOperationState,
		isAddNewRequestInProtocol,
		isUpdateRequest,
		showAdditionalRequestFlow,
		isTherapiesFlowEnabled,
	} = useRefundRequest();

	const {
		resetTherapies,
		isEmptyTherapies,
		modalEditDataAndCleanProcedures,
		closeModalEditDataAndCleanProcedures,
	} = useTherapies();

	const openModal =
		isTherapiesFlowEnabled && !isEmptyTherapies && modalEditDataAndCleanProcedures;

	const {isTablet, isMobile} = useMediaQueries();
	const accordionExpanded = useAccordionExpandedStore(state => state.accordion);
	const setAccordionExpanded = useAccordionExpandedStore(state => state.setAccordion);
	const [discardRequestModalState, setDiscardRequestModalState] = useState(false);
	const setStep = useStepStore(state => state.setStep);
	const {data: bankData} = useGetBeneficiaryBankDataQuery();
	const {planBeneficiaries} = useAccountDataStore(store => store.accountData);
	const scrollToTopRef = document.getElementById('content-container');
	const {dataHostNameState} = useHostName();
	const bannerMobileAppCookieName = `${dataHostNameState.product}.bannerMobileApp`;
	const hasBannerMobileApp = !Cookies.get(bannerMobileAppCookieName);
	const bannerMobileAppHeight = isMobile && hasBannerMobileApp ? 49 : 0;

	const isUpdate = requests?.protocol && isUpdateRequest;

	const {isEnabled: isAlphanumericDocumentEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_REFUND_ALPHANUMERIC_DOCUMENT,
	);

	const {isEnabled: isPreviousPlansEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_REFUND_PREVIOUS_PLANS,
	);

	const providerDataMethods = useForm<ProviderDataSchema>({
		resolver: zodResolver(providerDataSchema(isAlphanumericDocumentEnabled)),
		defaultValues: formAtoms?.providerDataForm,
		mode: 'onSubmit',
	});

	const patientDataMethods = useForm<PatientDataSchema>({
		resolver: zodResolver(patientDataSchema(planBeneficiaries, isPreviousPlansEnabled)),

		defaultValues: formAtoms?.patientDataForm,
		mode: 'onChange',
	});

	const fiscalDocumentMethods = useForm<FiscalDocumentSchema>({
		resolver: zodResolver(fiscalDocumentSchema),
		defaultValues: formAtoms?.fiscalDocumentDataForm,
		mode: 'onSubmit',
		shouldFocusError: true,
	});

	const handleGoTo = () => {
		setStep(
			isTherapiesFlowEnabled
				? StepEnum.THERAPIES
				: (nextStepWhenNoTherapies ?? StepEnum.SEND_DOCUMENTS),
		);
	};

	const handleGoBackToReview = () => {
		if (isAddNewRequestInProtocol) {
			handleDeleteLastRequest();
		}
		setStep(StepEnum.REVIEW);
	};

	const onProviderDataSubmit = async (data: ProviderDataSchema, isGoToReview: boolean) => {
		let {providerCode} = data;
		const document =
			data?.professionalDocument.length > 0
				? data?.professionalDocument
				: data?.professionalCpf;
		if (data?.searchDoctorBy === SearchDoctorByEnum.DOCUMENT && document) {
			if (data?.professionalType === PersonTypeEnum.PHYSICAL_PERSON) {
				const isCPFValid = validateCPF(document);
				if (!isCPFValid) {
					return;
				}
			} else {
				const isCNPJValid = validateCNPJ(document, isAlphanumericDocumentEnabled);
				if (!isCNPJValid) {
					return;
				}
				providerCode = data?.professionalCode || '';
			}

			providerDataMethods.clearErrors('professionalDocument');
		}

		if (
			data?.searchDoctorBy === SearchDoctorByEnum.COUNCIL_NUMBER &&
			document &&
			data?.doctorNotFound
		) {
			if (data?.professionalType === PersonTypeEnum.PHYSICAL_PERSON) {
				const isCPFValid = validateCPF(document);
				if (!isCPFValid) {
					providerDataMethods.setError('professionalDocument', {message: invalidCPF});
					return;
				}
			} else {
				const isCNPJValid = validateCNPJ(document, isAlphanumericDocumentEnabled);
				if (!isCNPJValid) {
					providerDataMethods.setError('professionalDocument', {message: invalidCNPJ});
					return;
				}
			}
		}

		const formattedData = {
			councilAcronym: data?.councilAcronym?.acronym ?? '',
			councilNumber: data?.councilNumber ?? '',
			councilState: data?.councilState.code ?? '',
			professionalName: data?.professionalName ?? '',
			professionalType:
				data?.professionalType === PersonTypeEnum.PHYSICAL_PERSON
					? PersonTypeEnum.F
					: PersonTypeEnum.J,
			providerCode,
			providerName:
				data?.providerName !== '' ? data?.providerName : (data?.professionalName ?? ''),
			professionalDocument:
				data?.professionalType === PersonTypeEnum.LEGAL_PERSON
					? (document.replace(/[^A-Za-z0-9]/g, '').toUpperCase() ?? '')
					: (document.replace(/\D/g, '') ?? ''),
		};

		handleUpdateFormAtoms.providerData(data);
		handleUpdateAtoms.professionalData(formattedData);

		if (isGoToReview) {
			const updateRequest = await handleUpdateRequest({professional: formattedData});
			if (updateRequest && updateRequest.protocol) {
				setStep(StepEnum.REVIEW);
				return;
			}
			return;
		}

		setAccordionExpanded(AccordionExpandedEnum.PATIENT_DATA);
	};

	const onPatientDataSubmit = async (data: PatientDataSchema, isGoToReview: boolean) => {
		handleUpdateFormAtoms.patientData(data);
		if (data.serviceDate !== null) {
			const formattedData = {
				...atoms?.treatmentData,
				beneficiary: {
					code: data?.patient.userCode,
				},
				planCode: data?.plan?.planCode ? String(data.plan.planCode) : undefined,
				fullBeneficiary: data?.patient,
				date: format(parseDate(data?.serviceDate) as Date, DateFormatEnum.DATE_FORMAT_10),
				specialty: {
					description: data?.specialty.description,
					specialtyCodeCbo: data?.specialty.codeCbo ? data?.specialty.codeCbo : '',
					specialtyCode: data?.specialty.code,
				},
			};
			handleUpdateAtoms.treatmentData(formattedData);
			if (isGoToReview) {
				const updateRequest = await handleUpdateRequest({treatment: formattedData});
				if (updateRequest && updateRequest.protocol) {
					setStep(StepEnum.REVIEW);
					return;
				}
				return;
			}
			setAccordionExpanded(AccordionExpandedEnum.FISCAL_DOCUMENT);
		}
	};

	const onFiscalDocumentSubmit = async (data: FiscalDocumentSchema, isGoToReview: boolean) => {
		const invoiceType =
			data?.canEmitFiscalDocument === 'yes' ? InvoiceTypeEnum.EMIT : InvoiceTypeEnum.NO_EMIT;

		const price = data?.priceFiscalDocument;

		const commonData = {
			invoiceNumber: Number(data?.numFiscalDocument),
			invoiceType,
			invoiceAmount: price,
		};

		const canEmitPayload = {
			...commonData,
			invoiceIssueCity: data?.city?.city,
			invoiceIssueState: data?.state?.code,
			ibgeDocumentCity: data?.city?.ibgedocumentcity,
			invoiceDigit: data?.verifyCode,
		};

		const payloadFinal = data?.canEmitFiscalDocument === 'yes' ? canEmitPayload : commonData;

		handleUpdateFormAtoms.fiscalDocumentData(data);
		handleUpdateAtoms.financialData(payloadFinal);

		const formattedBankData = {
			...bankData!,
			accountDigit: bankData?.accountDigit || '',
			agencyDigit: bankData?.agencyDigit || '',
		};

		resetTherapies();
		if (isUpdate) {
			const updateRequest = await handleUpdateRequest({
				financial: payloadFinal,
				bankData: formattedBankData,
			});
			if (updateRequest && updateRequest.protocol) {
				if (isGoToReview) {
					setStep(StepEnum.REVIEW);
					return;
				}
				handleGoTo();
				return;
			}

			return;
		}
		if (requests?.protocol && isAddNewRequestInProtocol) {
			const addNewRequest = await handleAddNewRequestInProtocol(
				payloadFinal,
				formattedBankData,
			);

			if (addNewRequest && addNewRequest.protocol) {
				handleGoTo();
				return;
			}

			return;
		}
		const createdRequest = await handleCreateRequest(payloadFinal, formattedBankData);

		if (createdRequest && createdRequest.protocol) {
			handleGoTo();
		}
	};

	const handleDiscardRequest = () => {
		setDiscardRequestModalState(true);
	};

	useEffect(() => {
		if (
			accordionExpanded === AccordionExpandedEnum.PATIENT_DATA ||
			accordionExpanded === AccordionExpandedEnum.FISCAL_DOCUMENT
		) {
			const offset = OFFSETS_TO_SCROLL[accordionExpanded];

			scrollToTopRef?.scrollTo({
				top: isTablet ? offset.tablet + bannerMobileAppHeight : offset.desktop,
				behavior: 'smooth',
			});
		}
	}, [accordionExpanded]);

	return (
		<Fragment>
			<CustomContainer>
				<StyledGrid.Root marginTop={isTablet ? 0 : 2} px={isTablet ? 0 : 3} spacing={2}>
					<CustomStyledGridItem md={12} lg={12}>
						<FormProvider {...providerDataMethods}>
							<ProviderData />
							{accordionExpanded === AccordionExpandedEnum.PROVIDER_DATA ? (
								<FooterButton
									isDisabled={isUpdateRequest && IS_LOADING}
									isLoading={IS_LOADING}
									primaryAction={providerDataMethods.handleSubmit(providerData =>
										onProviderDataSubmit(providerData, false),
									)}
									secondaryAction={
										isUpdate
											? providerDataMethods.handleSubmit(providerData =>
													onProviderDataSubmit(providerData, true),
												)
											: handleDiscardRequest
									}
									isAdditionalRequestFlow={showAdditionalRequestFlow}
									isEdit={isUpdateRequest}
								/>
							) : null}
						</FormProvider>
					</CustomStyledGridItem>
					<CustomStyledGridItem md={12} lg={12}>
						<FormProvider {...patientDataMethods}>
							<PatientData />
							{accordionExpanded === AccordionExpandedEnum.PATIENT_DATA ? (
								<FooterButton
									isDisabled={isUpdateRequest && IS_LOADING}
									isLoading={IS_LOADING}
									primaryAction={patientDataMethods.handleSubmit(patientData =>
										onPatientDataSubmit(patientData, false),
									)}
									secondaryAction={
										isUpdate
											? patientDataMethods.handleSubmit(patientData =>
													onPatientDataSubmit(patientData, true),
												)
											: handleDiscardRequest
									}
									isAdditionalRequestFlow={showAdditionalRequestFlow}
									isEdit={isUpdateRequest}
								/>
							) : null}
						</FormProvider>
					</CustomStyledGridItem>
					<CustomStyledGridItem md={12} lg={12}>
						<FormProvider {...fiscalDocumentMethods}>
							<FiscalDocument />
							{accordionExpanded === AccordionExpandedEnum.FISCAL_DOCUMENT ? (
								<FooterButton
									isDisabled={IS_LOADING}
									isLoading={IS_LOADING}
									primaryAction={fiscalDocumentMethods.handleSubmit(fiscalData =>
										onFiscalDocumentSubmit(fiscalData, false),
									)}
									secondaryAction={
										isUpdate
											? fiscalDocumentMethods.handleSubmit(fiscalData =>
													onFiscalDocumentSubmit(fiscalData, true),
												)
											: handleDiscardRequest
									}
									isAdditionalRequestFlow={showAdditionalRequestFlow}
									isEdit={isUpdateRequest}
									isFiscalDocumentStep
								/>
							) : null}
							<ErrorOnCreateRequestModal
								open={IS_ERROR}
								setOpen={() => setOperationState(OperationStateTypesEnum.PENDING)}
								onConfirm={fiscalDocumentMethods.handleSubmit(data =>
									onFiscalDocumentSubmit(data, false),
								)}
							/>
						</FormProvider>
					</CustomStyledGridItem>
				</StyledGrid.Root>
			</CustomContainer>
			<AbandonFlowModal
				open={discardRequestModalState}
				setOpen={setDiscardRequestModalState}
				handleConfirm={handleGoBackToReview}
				title={discardModal.title}
				headerTitle={discardModal.headerTitle}
				description={discardModal.description}
			/>
			<AlertModal
				footerContent={
					<AlertActions
						primaryAction={closeModalEditDataAndCleanProcedures}
						secondaryAction={() => {
							closeModalEditDataAndCleanProcedures();
							handleGoTo();
						}}
						secondaryText={editDataAndCleanProceduresModal.secondaryActionText}
						primaryText={editDataAndCleanProceduresModal.primaryActionText}
						flexDirection="column-reverse"
					/>
				}
				headerTitle={editDataAndCleanProceduresModal.headerTitle}
				title={editDataAndCleanProceduresModal.title}
				description={editDataAndCleanProceduresModal.description}
				open={openModal}
				onClose={() => {
					closeModalEditDataAndCleanProcedures();
					handleGoTo();
				}}
				variant="info"
				customIconName="info-filled"
			/>
			<StyledFooterContainer $hasDoubleButton={!!requests?.protocol}>
				<MainFooter />
			</StyledFooterContainer>
		</Fragment>
	);
};
